import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, Plus, Calendar, DollarSign, CheckCircle, Clock, ChevronRight, ChevronDown } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Panigation } from "@/components/ui/Panigation";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmProfitPayment, getListProfits } from "@/api/Admin/Profit";
import { ProfitAdminAPI } from "@/api/Admin/type";
import { message } from "antd";
import * as XLSX from "xlsx";

type PaymentStatus = "paid" | "pending" | "created";
type PackageType = "TRADER" | "PIONEER" | "TITAN";
type PackageTerm = "short" | "medium" | "long";

type PackagePayment = {
  id: string;
  user_package_id: string;
  packageName: string;
  packageCode: string;
  packageType: PackageType;
  packageTerm: PackageTerm;
  paymentPeriod: string;
  interestAmount: number;
  taxAmount: number;
  netAmount: number;
  dueDate: string;
  status: PaymentStatus;
};

type InvestorPayment = {
  investorId: string;
  investorName: string;
  investorCode: string;
  paymentPeriod: string;
  totalInterest: number;
  totalTax: number;
  totalNet: number;
  packages: PackagePayment[];
};

const EXPORT_HEADER_MAP = {
  NHA_DAU_TU: "Nhà đầu tư",
  MA_NHA_DAU_TU: "Mã nhà đầu tư",
  GOI_DAU_TU: "Gói đầu tư",
  KY_TRA_LAI: "Kỳ trả lãi",
  LAI_PHAI_TRA: "Lãi phải trả",
  THUE_KHAU_TRU: "Thuế khấu trừ",
  LAI_THUC_NHAN: "Lãi thực nhận",
  NGAY_DEN_HAN: "Ngày đến hạn",
  TRANG_THAI: "Trạng thái",
  MA_CHI_TIET: "Mã chi tiết",
  MA_GOI_NGUOI_DUNG: "Mã gói người dùng",
  STT_GOI: "STT gói",
} as const;

type ExportColumnKey = keyof typeof EXPORT_HEADER_MAP;

const EXPORT_SUMMARY_COLUMNS: ExportColumnKey[] = ["LAI_PHAI_TRA", "THUE_KHAU_TRU", "LAI_THUC_NHAN"];

const mapProfitResponseToInvestors = (response: ProfitAdminAPI): InvestorPayment[] => {
  return (response.data?.data || []).map((item) => {
    const packages: PackagePayment[] = item.items.map((pkg) => ({
      id: pkg.id.toString(),
      user_package_id: pkg.user_package_id.toString(),
      packageName: pkg.package_name,
      packageCode: "N/A",
      packageType: "TITAN",
      packageTerm: "long",
      paymentPeriod: pkg.period,
      interestAmount: Number(pkg.gross_amount),
      taxAmount: Number(pkg.tax_fee),
      netAmount: Number(pkg.net_amount),
      dueDate: pkg.due_date,
      status: pkg.status.toLowerCase() === "pending" ? "pending" : "paid",
    }));

    const totalInterest = packages.reduce((sum, profitPackage) => sum + profitPackage.interestAmount, 0);
    const totalTax = packages.reduce((sum, profitPackage) => sum + profitPackage.taxAmount, 0);
    const totalNet = packages.reduce((sum, profitPackage) => sum + profitPackage.netAmount, 0);

    return {
      investorId: item.user_id.toString(),
      investorName: item.user_info.fullname,
      investorCode: item.user_info.cccd_number || `BEQ${item.user_id}`,
      paymentPeriod: item.items.length > 0 ? item.items[0].period : "",
      totalInterest,
      totalTax,
      totalNet,
      packages,
    };
  });
};





const InterestPayment: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [monthFilter, setMonthFilter] = useState("");
  const [packageFilter, setPackageFilter] = useState("");
  const [expandedInvestors, setExpandedInvestors] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [investorData, setInvestorData] = useState<InvestorPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [perPageFromApi, setPerPageFromApi] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [exporting, setExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState<ExportColumnKey[]>(
    Object.keys(EXPORT_HEADER_MAP) as ExportColumnKey[]
  );
  const [exportRowLimit, setExportRowLimit] = useState<number | null>(200);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page: currentPage, limit: itemsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (packageFilter) params.package_name = packageFilter;
      if (monthFilter) {
        params.period = monthFilter;
      }

      const res = await getListProfits(params);
      if (res?.data?.data) {
        setTotalCount(res.data.total || 0);
        setPerPageFromApi(res.data.per_page || 10);
        setInvestorData(mapProfitResponseToInvestors(res));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, packageFilter, monthFilter, currentPage, itemsPerPage, t]);

  const stats = useMemo(() => {
    let totalPayments = 0;
    let totalAmount = 0;
    let paidCount = 0;
    let pendingCount = 0;

    investorData.forEach((investor) => {
      investor.packages.forEach((pkg) => {
        totalPayments++;
        totalAmount += pkg.netAmount;
        if (pkg.status === "paid") paidCount++;
        else pendingCount++;
      });
    });

    return { totalPayments, totalAmount, paidCount, pendingCount };
  }, [investorData]);

  const toggleExpand = (investorId: string) => {
    setExpandedInvestors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(investorId)) {
        newSet.delete(investorId);
      } else {
        newSet.add(investorId);
      }
      return newSet;
    });
  };

  const totalPages = Math.ceil(totalCount / perPageFromApi);
  const paginatedData = investorData;

  const openPaymentDetails = (
    paymentId: string,
    investorName: string,
    investorId: string,
    paymentPeriod: string
  ) => {
    navigate(`/admin/interest-payments/${paymentId}`, {
      state: { investorName, investorId, paymentPeriod },
    });
  };

  const handleCreateBatch = () => {
    navigate("/admin/interest-payments/create-batch");
  };

  const handleOpenExportModal = () => {
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleExportLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (!nextValue.trim()) {
      setExportRowLimit(null);
      return;
    }

    const parsedValue = Number(nextValue);
    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      return;
    }

    setExportRowLimit(parsedValue);
  };

  const handleToggleExportColumn = (column: ExportColumnKey) => {
    setSelectedExportColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((item) => item !== column);
      }

      return [...prev, column];
    });
  };

  const handleExport = async () => {
    if (selectedExportColumns.length === 0) {
      message.warning("Vui lòng chọn ít nhất một cột để xuất");
      return;
    }

    setExporting(true);
    try {
      const baseParams = {
        limit: 200,
        search: debouncedSearch || undefined,
        package_name: packageFilter || undefined,
        period: monthFilter || undefined,
      };

      const firstPage = await getListProfits({
        page: 1,
        ...baseParams,
      });
      const firstPageData = firstPage.data;
      const lastPage = firstPageData?.last_page || 1;
      const allInvestors = mapProfitResponseToInvestors(firstPage);

      if (lastPage > 1) {
        const pageRequests: Promise<ProfitAdminAPI>[] = [];
        for (let pageIndex = 2; pageIndex <= lastPage; pageIndex += 1) {
          pageRequests.push(
            getListProfits({
              page: pageIndex,
              ...baseParams,
            })
          );
        }

        const pageResults = await Promise.all(pageRequests);
        pageResults.forEach((result) => {
          allInvestors.push(...mapProfitResponseToInvestors(result));
        });
      }

      const rawExportRows = allInvestors.flatMap((investor) => {
        return investor.packages.map((profitPackage, index) => ({
          NHA_DAU_TU: investor.investorName,
          MA_NHA_DAU_TU: investor.investorCode,
          GOI_DAU_TU: profitPackage.packageName,
          KY_TRA_LAI: profitPackage.paymentPeriod,
          LAI_PHAI_TRA: profitPackage.interestAmount,
          THUE_KHAU_TRU: profitPackage.taxAmount,
          LAI_THUC_NHAN: profitPackage.netAmount,
          NGAY_DEN_HAN: profitPackage.dueDate,
          TRANG_THAI: profitPackage.status,
          MA_CHI_TIET: profitPackage.id,
          MA_GOI_NGUOI_DUNG: profitPackage.user_package_id,
          STT_GOI: index + 1,
        }));
      });

      const exportRows = (exportRowLimit ? rawExportRows.slice(0, exportRowLimit) : rawExportRows).map((row) => {
        const filteredRow = {} as Record<ExportColumnKey, string | number>;
        selectedExportColumns.forEach((column) => {
          filteredRow[column] = row[column];
        });
        return filteredRow;
      });

      if (exportRows.length === 0) {
        message.info("Không có dữ liệu để xuất Excel");
        return;
      }

      const columns = selectedExportColumns;
      const totalRow = columns.reduce((accumulator, column, index) => {
        if (index === 0) {
          accumulator[column] = "Tổng";
          return accumulator;
        }

        if (EXPORT_SUMMARY_COLUMNS.includes(column)) {
          accumulator[column] = exportRows.reduce((sum, row) => sum + Number(row[column] || 0), 0);
          return accumulator;
        }

        accumulator[column] = "";
        return accumulator;
      }, {} as Record<ExportColumnKey, string | number>);

      const rowsWithTotal = [...exportRows, totalRow];

      const worksheet = XLSX.utils.json_to_sheet(rowsWithTotal, { header: columns });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Interest Payments");
      XLSX.utils.sheet_add_aoa(worksheet, [columns.map((column) => EXPORT_HEADER_MAP[column])], { origin: "A1" });

      const now = new Date();
      const fileName = `interest_payments_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success(`Xuất Excel thành công (${exportRows.length} khoản trả lãi)`);
      setIsExportModalOpen(false);
    } catch (error) {
      console.error("Export interest payments error:", error);
      message.error("Xuất Excel thất bại, vui lòng thử lại");
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: PaymentStatus) => {
    const badges = {
      paid: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        dot: "bg-green-500",
        label: t("interestPayment.statusPaid"),
      },
      created: {
        bg: "bg-yellow-100 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-400",
        dot: "bg-yellow-500",
        label: t("interestPayment.statusCreated"),
      },
      pending: {
        bg: "bg-gray-100 dark:bg-gray-700/30",
        text: "text-gray-700 dark:text-gray-400",
        dot: "bg-gray-500",
        label: t("interestPayment.statusPending"),
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        <span className={`w-2 h-2 rounded-full ${badge.dot}`}></span>
        {badge.label}
      </span>
    );
  };

  const getPackageTermBadge = (term: PackageTerm) => {
    const badges = {
      short: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      long: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    };
    const labels = {
      short: "ngắn hạn",
      medium: "trung hạn",
      long: "dài hạn",
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${badges[term]}`}
      >
        {labels[term]}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + " đ";
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("interestPayment.title") },
  ];

  // Generate month options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    // Generate options from +6 months (future) to -6 months (past)
    for (let i = -6; i <= 6; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      options.push({
        value: `${year}-${month}`,
        label: `Tháng ${month}/${year}`,
      });
    }
    return options;
  };

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("interestPayment.title")} - Admin Dashboard</title>
      </Helmet>
      <ToastContainer />

      <PageHeader
        title={t("interestPayment.title")}
        description={t("interestPayment.subtitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("interestPayment.totalPayments")}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.totalPayments}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-md">
              <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("interestPayment.totalAmount")}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats.totalAmount)}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-md">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("interestPayment.paidCount")}
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {stats.paidCount}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-md">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("interestPayment.pendingCount")}
              </p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
                {stats.pendingCount}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-md">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Buttons Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("interestPayment.allMonths")}</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("interestPayment.allPackages")}</option>
            <option value="TRADER">Trader</option>
            <option value="PIONEER">Pioneer</option>
            <option value="TITAN">Titan</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCreateBatch}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            {t("interestPayment.createBatch")}
          </button>
          <button
            onClick={handleOpenExportModal}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-primary text-primary hover:bg-primary hover:text-white dark:border-primary dark:text-primary rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Đang xuất..." : t("interestPayment.export")}
          </button>
        </div>
      </div>

      {/* Search Row */}
      <div className="mb-6">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t("interestPayment.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap w-10">
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.investor")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.numberOfPackages")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.paymentPeriod")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.interestAmount")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.taxAmount")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t("interestPayment.netAmount")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center">
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size={32} /> {t("common.loading")}...
                    </span>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    {t("interestPayment.noData")}
                  </td>
                </tr>
              ) : (
                paginatedData.map((investor) => (
                  <React.Fragment key={investor.investorId}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                      onClick={() => toggleExpand(investor.investorId)}
                    >
                      <td className="px-3 py-4">
                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors">
                          {expandedInvestors.has(investor.investorId) ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {investor.investorName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {investor.investorCode}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-medium text-primary dark:text-primary">
                          {investor.packages.length} gói
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-gray-700 dark:text-gray-300">
                        {investor.paymentPeriod}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-bold text-gray-900 dark:text-white">
                        {formatCurrency(investor.totalInterest)}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-medium text-red-500 dark:text-red-400">
                        {formatCurrency(investor.totalTax)}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(investor.totalNet)}
                      </td>
                    </tr>

                    {/* Nested Table */}
                    {expandedInvestors.has(investor.investorId) && (
                      <tr className="bg-gray-50/50 dark:bg-gray-700/20">
                        <td colSpan={7} className="px-0 py-0">
                          <div className="px-4 py-2">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-600">
                                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    TÊN GÓI
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    KỲ TRẢ LÃI
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    LÃI PHẢI TRẢ
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    THUẾ KHẤU TRỪ
                                  </th>
                                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    LÃI THỰC NHẬN
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    NGÀY ĐẾN HẠN
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    TRẠNG THÁI
                                  </th>
                                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    THAO TÁC
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {investor.packages.map((pkg) => (
                                  <tr
                                    key={pkg.id}
                                    className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-100/50 dark:hover:bg-gray-600/20 transition-colors"
                                  >
                                    <td className="px-3 py-3">
                                      <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {pkg.packageName}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                                      {pkg.paymentPeriod}
                                    </td>
                                    <td className="px-3 py-3 text-right text-sm text-gray-900 dark:text-white">
                                      {formatCurrency(pkg.interestAmount)}
                                    </td>
                                    <td className="px-3 py-3 text-right text-sm text-red-500 dark:text-red-400">
                                      {formatCurrency(pkg.taxAmount)}
                                    </td>
                                    <td className="px-3 py-3 text-right text-sm font-semibold text-green-600 dark:text-green-400">
                                      {formatCurrency(pkg.netAmount)}
                                    </td>
                                    <td className="px-3 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                                      {pkg.dueDate}
                                    </td>
                                    <td className="px-3 py-3 text-center whitespace-nowrap">
                                      {getStatusBadge(pkg.status)}
                                    </td>
                                    <td className="px-3 py-3 text-center whitespace-nowrap space-x-2 flex justify-center">
                                      <button
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary hover:bg-primary dark:hover:bg-primary/20 rounded-md transition-colors whitespace-nowrap"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openPaymentDetails(
                                            pkg.user_package_id,
                                            investor.investorName,
                                            investor.investorId,
                                            pkg.paymentPeriod
                                          );
                                        }}
                                      >
                                        {t("interestPayment.viewDetail")}
                                      </button>
                                      <button
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary hover:bg-primary dark:hover:bg-primary/20 rounded-md transition-colors whitespace-nowrap"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await confirmProfitPayment(Number(pkg.id))
                                            .then(() => {
                                              message.success(t("interestPayment.confirmSuccess"));
                                              fetchData();
                                            })
                                            .catch(() => {
                                              message.error(t("common.error"));
                                            });
                                        }}
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Panigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {isExportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tuy chon xuat Excel</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Chon cot du lieu va gioi han so dong can tai xuong.</p>
              </div>
              <button
                type="button"
                onClick={handleCloseExportModal}
                className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                <span className="text-lg leading-none">x</span>
              </button>
            </div>

            <div className="space-y-6 px-6 py-5">
              <div>
                <label htmlFor="export-row-limit" className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                  So dong toi da
                </label>
                <input
                  id="export-row-limit"
                  type="number"
                  min={1}
                  value={exportRowLimit ?? ""}
                  onChange={handleExportLimitChange}
                  placeholder="Nhap so dong can xuat"
                  className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">De trong de xuat toan bo du lieu da tai duoc.</p>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Cot du lieu</p>
                  <div className="flex items-center gap-3 text-sm font-medium">
                    <button
                      type="button"
                      className="text-primary"
                      onClick={() => setSelectedExportColumns(Object.keys(EXPORT_HEADER_MAP) as ExportColumnKey[])}
                    >
                      Chon tat ca
                    </button>
                    <button
                      type="button"
                      className="text-gray-500 dark:text-gray-400"
                      onClick={() => setSelectedExportColumns([])}
                    >
                      Bo chon
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(Object.keys(EXPORT_HEADER_MAP) as ExportColumnKey[]).map((column) => (
                    <label
                      key={column}
                      className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition-colors hover:border-primary/50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedExportColumns.includes(column)}
                        onChange={() => handleToggleExportColumn(column)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{EXPORT_HEADER_MAP[column]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCloseExportModal}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Dong
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {exporting ? "Dang xuat..." : "Xuat Excel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestPayment;
